import { Link } from "react-router-dom";
import { Award, Trophy } from "lucide-react";
import { Bloc, Barre, Vide } from "./commun";

export const Performance = ({ p }) => (
  <Bloc titre="Performance par fichier et par opération" sous="30 derniers jours" testId="team-performance">
    <p className="at-label mb-1.5">Par fichier</p>
    {p.fichiers.length === 0 && <Vide />}
    <div className="space-y-1.5 mb-4">
      {p.fichiers.map((f) => (
        <div key={f.nom} className="at-soft p-2.5 text-sm" data-testid="performance-file-row">
          <p className="font-semibold break-words">{f.nom}</p><p className="text-xs at-muted">{f.appels} appels · {f.decroches} décrochés · {f.opportunites} opportunité(s)</p>
        </div>
      ))}
    </div>
    <p className="at-label mb-1.5">Par opération</p>
    {p.operations.length === 0 && <Vide />}
    <div className="space-y-1.5">
      {p.operations.map((o) => (
        <div key={o.operation} className="at-soft p-2.5 text-sm flex justify-between gap-2" data-testid="performance-operation-row">
          <span className="font-semibold truncate">{o.operation}</span><span className="text-xs shrink-0 tabular-nums">{o.opportunites} opp. · {o.validees} val. · {o.signees} sig.</span>
        </div>
      ))}
    </div>
  </Bloc>
);

export const Objectifs = ({ liste, challenges, formulaire }) => (
  <Bloc titre="Objectifs, badges et challenges" testId="team-goals">
    <div className="space-y-3 mb-4">
      {liste.map((o) => (
        <div key={o.agent_id} className="at-soft p-3 space-y-1.5" data-testid={`goal-row-${o.agent_id}`}>
          <p className="font-semibold">{o.agent}</p>
          {[["Appels", o.appels, o.objectif_appels], ["Décrochés", o.decroches, o.objectif_decroches], ["Opportunités", o.opportunites, o.objectif_opportunites]].map(([l, v, obj]) => (
            <div key={l}><div className="flex justify-between text-xs"><span>{l}</span><span className="tabular-nums">{v} / {obj}</span></div><Barre pct={obj ? (100 * v) / obj : 0} couleur={v >= obj ? "var(--success)" : "var(--accent)"} /></div>
          ))}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {o.badges.length === 0 && <span className="text-xs at-muted">Pas encore de badge.</span>}
            {o.badges.map((b) => <span key={b.code} className="text-[11px] at-pill px-2 py-1 flex items-center gap-1" style={{ background: "var(--surface)" }} data-testid={`badge-${o.agent_id}-${b.code}`}><Award size={12} />{b.nom}</span>)}
          </div>
        </div>
      ))}
    </div>
    <p className="at-label mb-1.5">Challenges en cours</p>
    {challenges.length === 0 && <Vide texte="Aucun challenge en cours." testId="challenges-empty" />}
    <div className="space-y-2 mb-3">
      {challenges.map((c) => (
        <div key={c.id} className="at-soft p-3 text-sm" data-testid={`challenge-${c.id}`}>
          <p className="font-semibold flex items-center gap-1.5"><Trophy size={14} />{c.titre}</p>
          <p className="text-xs at-muted">{c.indicateur_label} · objectif {c.objectif} · du {c.debut.split("-").reverse().join("/")} au {c.fin.split("-").reverse().join("/")}{c.recompense ? ` · ${c.recompense}` : ""}</p>
          {c.scores.map((s) => <p key={s.agent} className="text-xs flex justify-between"><span>{s.agent}</span><b style={{ color: s.atteint ? "var(--success)" : "var(--ink)" }}>{s.valeur} / {c.objectif}</b></p>)}
          {formulaire.supprimer && <button type="button" onClick={() => formulaire.supprimer(c.id)} className="text-xs font-semibold underline min-h-[44px]" data-testid={`challenge-delete-${c.id}`}>Retirer</button>}
        </div>
      ))}
    </div>
    {formulaire.element}
  </Bloc>
);

export const Carte = ({ liste }) => {
  const max = Math.max(1, ...liste.map((d) => d.nombre));
  return (
    <Bloc titre="Opportunités par département" sous="Depuis le début de l'année" testId="team-map">
      {liste.length === 0 && <Vide />}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
        {liste.map((d) => (
          <div key={d.departement} className="aspect-square rounded-xl flex flex-col items-center justify-center" data-testid={`map-dept-${d.departement}`}
            style={{ background: `color-mix(in srgb, var(--accent) ${Math.round(15 + (85 * d.nombre) / max)}%, var(--surface-2))`, color: d.nombre / max > 0.5 ? "var(--accent-ink)" : "var(--ink)" }}>
            <span className="text-sm font-bold">{d.departement}</span><span className="text-[11px]">{d.nombre}</span>
          </div>
        ))}
      </div>
    </Bloc>
  );
};

export const Integration = ({ liste }) => (
  <Bloc titre="Intégration des nouveaux agents" sous="Semaine par semaine (12 premières semaines)" testId="team-onboarding">
    {liste.length === 0 && <Vide texte="Aucun agent arrivé depuis moins de 12 semaines." />}
    <div className="space-y-3">
      {liste.map((a) => (
        <div key={a.agent_id} data-testid={`onboarding-${a.agent_id}`}>
          <p className="font-semibold text-sm">{a.agent}</p>
          <div className="space-y-1 mt-1">
            {a.semaines.map((s) => <p key={s.semaine} className="text-xs at-soft px-2.5 py-1.5 flex justify-between gap-2"><span>Semaine {s.semaine}</span><span className="tabular-nums">{s.appels} appels · décroché {s.taux_decroche ?? "—"} % · {s.opportunites} opp.</span></p>)}
          </div>
        </div>
      ))}
    </div>
  </Bloc>
);

export const Retours = ({ liste }) => (
  <Bloc titre="Retours des partenaires sur les leads" sous="Remontés à l'agent d'origine" testId="partner-feedback">
    {liste.length === 0 && <Vide texte="Aucun retour pour le moment." testId="partner-feedback-empty" />}
    <div className="space-y-1.5">
      {liste.map((r) => (
        <Link key={r.fiche_id} to={`/fiches/${r.fiche_id}`} className="at-soft p-2.5 text-sm flex justify-between gap-2 min-h-[44px] items-center" data-testid={`feedback-row-${r.fiche_id}`}>
          <span className="min-w-0"><span className="font-semibold block truncate">{r.nom}</span><span className="text-xs at-muted truncate block">{r.partenaire} · {r.agent}{r.commentaire ? ` · ${r.commentaire}` : ""}</span></span>
          <span className="text-xs font-bold shrink-0" style={{ color: r.retour === "signe" ? "var(--success)" : "var(--danger)" }}>{r.label}</span>
        </Link>
      ))}
    </div>
  </Bloc>
);
